"use client";
import React, { createContext, useContext, useCallback, useState } from "react";
import { BASE_URL } from "../config/config";

const FaqContext = createContext();

export const FaqProvider = ({ children }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  // 🔹 FETCH FAQs
  const fetchFaqs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/faqs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.status === 200) {
        // Validate and clean the FAQ data
        const faqsData = data.data || [];
        const validatedFaqs = faqsData.map(faq => ({
          id: faq.id,
          question: faq.question || '',
          answer: faq.answer || '',
          status: faq.status || 'Active',
          ...faq
        }));
        setFaqs(validatedFaqs);
      } else {
        setError(data.message || "Failed to fetch FAQs");
      }
    } catch (err) {
      console.error("Fetch FAQs error:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔹 ADD FAQ
  const addFaq = async (formData) => {
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        // Prepare payload exactly as backend expects
        const payload = {
        question: formData.question,       // required
        answer: formData.answer,           // required
        status: formData.status || 'Active', // include status
        create_audit_id: user.id,          // required
        };

        console.log("Adding FAQ with payload:", payload);

        const res = await fetch(`${BASE_URL}/faqs`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        });
        const data = await res.json();

        console.log("Add FAQ response:", data);

        if (res.ok && (data.status === 200 || data.status === 201)) {
        // Since backend only returns ID, construct the FAQ object using form data
        const newFaq = data.data;
        if (newFaq && newFaq.id) {
          // Use the original form data with the returned ID
          const validatedFaq = {
            id: newFaq.id,
            question: formData.question || '',
            answer: formData.answer || '',
            status: formData.status || 'Active',
            // Add any other fields from the response
            ...newFaq
          };
          console.log("Adding validated FAQ to state:", validatedFaq);
          setFaqs((prev) => [validatedFaq, ...prev]);
          return true;
        } else {
          console.error('No ID returned from server:', newFaq);
          alert("Invalid response from server");
          return false;
        }
        } else {
        console.error("Add FAQ failed:", data);
        alert(data.message || "Add failed");
        return false;
        }
    } catch (err) {
        console.error("Network or other error:", err);
        alert("Network error");
        return false;
    }
    };


  // 🔹 UPDATE FAQ
  const updateFaq = async (id, payload) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.update_audit_id = user.id;

      console.log("Updating FAQ with payload:", payload);

      const res = await fetch(`${BASE_URL}/faqs/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      console.log("Update FAQ response:", data);

      if (res.ok && data.status === 200) {
        // Validate the updated data
        const validatedPayload = {
          question: payload.question || '',
          answer: payload.answer || '',
          status: payload.status || 'Active',
          ...payload
        };
        setFaqs((prev) =>
          prev.map((f) => (f.id === id ? { ...f, ...validatedPayload } : f))
        );
        return true;
      } else {
        console.error("Update FAQ failed:", data);
        alert(data.message || "Update failed");
      }
    } catch (err) {
      console.error("Update FAQ error:", err);
      alert("Network error");
    }
  };

  // 🔹 DELETE FAQ
  const deleteFaq = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${BASE_URL}/faqs/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id: user.id }),
      });

      const data = await res.json();
      console.log("Delete FAQ response:", data);

      if (res.ok && data.status === 200) {
        setFaqs((prev) => prev.filter((f) => f.id !== id));
      } else {
        console.error("Delete FAQ failed:", data);
        alert(data.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete FAQ error:", err);
      alert("Delete failed");
    }
  };

  // 🔹 STATUS TOGGLE
  const toggleFaqStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${BASE_URL}/faqs/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus, update_audit_id: user.id }),
      });

      const data = await res.json();
      console.log("Toggle FAQ status response:", data);

      if (res.ok && data.status === 200) {
        setFaqs((prev) =>
          prev.map((f) => (f.id === id ? { ...f, status: newStatus } : f))
        );
      } else {
        console.error("Toggle FAQ status failed:", data);
        alert(data.message || "Status update failed");
      }
    } catch (err) {
      console.error("Toggle FAQ status error:", err);
      alert("Status update failed");
    }
  };

  return (
    <FaqContext.Provider
      value={{
        faqs,
        loading,
        error,
        fetchFaqs,
        addFaq,
        updateFaq,
        deleteFaq,
        toggleFaqStatus,
      }}
    >
      {children}
    </FaqContext.Provider>
  );
};

export const useFaqs = () => useContext(FaqContext);
